import type { Column, Table } from '@tanstack/react-table';
import { type ReactNode, useState } from 'react';
import { DataTableHeaderCell } from '../../_base/index.js';
import { DataTableHeaderCellList } from '../../_base/list/index.js';
import { DataTableHeaderCellSurface } from '../../_base/surface/index.js';
import { useDateListFilter } from '../../_hooks/list/date/index.js';
import { useOpenPopover } from '../../_hooks/open-popover.js';
import { ResetFilterMenuItem } from '../../_shared/reset.js';
import { DATE_FILTER_OPERATION, type DateFilterValuesWithoutInArray } from '../filter/operation.js';
import type { DateFilterSchemaWithoutInArray } from '../filter/schema.js';
import { DROPDOWN_DATE_OPTIONS } from './data.js';
import { DateFilter } from './filter.js';
import { DateOperation } from './operation.js';

type DataTableHeaderCellDateProps<TData, TValue> = {
    tableId: string;
    table: Table<TData>;
    column: Column<TData, TValue>;
    title: ReactNode;
    tooltip: ReactNode;
};

export function DataTableHeaderCellDate<TData, TValue>({
    tableId,
    table,
    column,
    title,
    tooltip,
}: DataTableHeaderCellDateProps<TData, TValue>) {
    const filter = column.getFilterValue() as DateFilterSchemaWithoutInArray | undefined;

    const [dropdownValue, setDropdownValue] = useState<DateFilterValuesWithoutInArray['id']>(
        filter?.operation ?? DROPDOWN_DATE_OPTIONS[0][0]
    );

    const { open, openPopover, closePopover, onOpenChange } = useOpenPopover();

    const {
        disabled: isListFilterDisabled,
        data,
        selectedItems,
        handleSelectionChange,
        onApplyFilter,
        resetListFilter,
    } = useDateListFilter({
        tableId,
        table,
        column,
        setDropdownValue,
    });

    return (
        <DataTableHeaderCell
            column={column}
            title={title}
            open={open}
            openPopover={openPopover}
            closePopover={closePopover}
            onOpenChange={onOpenChange}
            tooltip={tooltip}
            popover={
                <DataTableHeaderCellSurface
                    column={column}
                    closePopover={closePopover}
                    filterNode={
                        <>
                            <ResetFilterMenuItem
                                column={column}
                                closePopover={closePopover}
                                resetOperation={resetListFilter}
                            />

                            <DateOperation
                                column={column}
                                dropdownValue={dropdownValue}
                                resetListFilter={resetListFilter}
                                setDropdownValue={setDropdownValue}
                            />

                            {dropdownValue ===
                            (DATE_FILTER_OPERATION.inArray.id as DateFilterValuesWithoutInArray['id']) ? null : (
                                <DateFilter column={column} dropdownValue={dropdownValue} />
                            )}

                            {!isListFilterDisabled && (
                                <DataTableHeaderCellList
                                    data={data}
                                    selectedItems={selectedItems}
                                    handleSelectionChange={handleSelectionChange}
                                    onApplyFilter={onApplyFilter}
                                />
                            )}
                        </>
                    }
                />
            }
        />
    );
}
